import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { useOrganization } from './organization';
import { handleApiError } from '../api-client/error_handler';
import axios from 'axios';

interface ApiKey {
  api_key: string;
  date_created: string;
  used_quota: string;
  remaining_quota: number;
}

interface ApiKeyContextProps {
  apiKeys: ApiKey[];
  refreshApiKeys: () => Promise<void>;
  loading: boolean;
}

const ApiKeyContext = createContext<ApiKeyContextProps | undefined>(undefined);

export const ApiKeyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { organization, accountLinked } = useOrganization();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchApiKeys = useCallback(async () => {
    if (!isAuthenticated || !organization || accountLinked == null) return;

    try {
      const token = await getAccessTokenSilently({ cacheMode: accountLinked ? 'off' : 'on' });
      const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api-keys`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setApiKeys(data.apiKeys ?? []);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, organization, getAccessTokenSilently]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  return (
    <ApiKeyContext.Provider value={{ apiKeys, refreshApiKeys: fetchApiKeys, loading }}>
      {children}
    </ApiKeyContext.Provider>
  );
};

export const useApiKeys = (): ApiKeyContextProps => {
  const context = useContext(ApiKeyContext);
  if (!context) {
    throw new Error('useApiKeys must be used within an ApiKeyProvider');
  }
  return context;
};
